const all = new Map<string, GeckoAggregation>()

/**
 * Binned data requires data points within a bin to be aggregated into a single
 * value. Often, this is a simple average, though there are a number of
 * reasonable alternatives depending on the particular skew of the data.
 */
export class GeckoAggregation {
  //----------------------------------------------------------------------------

  public static readonly Description =
    'How individual data points are aggregated per bin'

  //----------------------------------------------------------------------------

  public static readonly Default = new GeckoAggregation(
    'thickness',
    'Default',
    "The default aggregation used to calculate a bin's measurement"
  )
  public static readonly Minimum = new GeckoAggregation(
    'min_thickness',
    'Minimum',
    'Calculate bin measurement by using the minimum reading'
  )
  public static readonly Mean = new GeckoAggregation(
    'avg_thickness',
    'Average',
    'Calculate bin measurement by using the mean reading'
  )
  public static readonly AverageLow30 = new GeckoAggregation(
    'avg_low30_thickness',
    'Low 30 Average',
    'Calculate bin measurement by using the mean reading of the lowest 30'
  )
  public static readonly ComponentMean = new GeckoAggregation(
    'component_mean',
    'Component-wide mean',
    'Calculate bin measurement by averaging across the entire component'
  )
  public static readonly ThicknessLessCoating = new GeckoAggregation(
    'thickness-less-coating',
    'Thickness Less Coating',
    'Thickness values with the coating factored out.'
  )
  public static readonly None = new GeckoAggregation(
    'no-aggregation',
    'None',
    'No aggregation applied'
  )

  //----------------------------------------------------------------------------

  /** A unique ID which should line up with Data Systems and even Portal */
  public readonly id: string

  /** A user-facing name for this object, or its ID if none is populated */
  public readonly name: string

  /** A user-facing description for this a specific data aggregation */
  public readonly description: string

  private constructor(id: string, name: string, description: string) {
    this.id = id
    this.name = name
    this.description = description
    all.set(id, this)
  }

  //----------------------------------------------------------------------------

  /**
   * The default sort for aggregation objects
   * @param a The first aggregation object
   * @param b The second aggregation object
   */
  public static DefaultSort(a: GeckoAggregation, b: GeckoAggregation): number {
    return defaultSortOrder.indexOf(a.id) - defaultSortOrder.indexOf(b.id)
  }

  /**
   * Returns the unique aggregation instance for a given ID if it exists
   * @param id The unique ID of this aggregation
   */
  public static GetByID(id: string | undefined): GeckoAggregation | undefined {
    return id === undefined
      ? GeckoAggregation.None
      : (all.get(id) ?? new GeckoAggregation(id, id, ''))
  }

  /**
   * Returns the unique aggregation instance for a given ID, or creates a
   * unique aggregation instance from scratch for this new ID
   * @param id The unique ID of this object
   */
  public static GetOrCreate(id: string | undefined): GeckoAggregation {
    return id === undefined
      ? GeckoAggregation.None
      : (all.get(id) ?? new GeckoAggregation(id, id, ''))
  }
}

const defaultSortOrder = [
  GeckoAggregation.Default.id,
  GeckoAggregation.None.id,
  GeckoAggregation.Minimum.id,
  GeckoAggregation.Mean.id,
  GeckoAggregation.AverageLow30.id,
]
